import { FC, useContext, useEffect } from 'react';

import { ResponsiveTabs } from 'src/components/ResponsiveTabs';
import { Client } from 'src/constants';
import { DRAWER_IDS, DrawerId, FlagsContext, RouteContext } from 'src/context';
import { InternalChat } from 'src/features/assistant/InternalChat';
import { ContextProperties } from 'src/features/context-bar/ContextProperties';
import { DrawerContentHeader } from 'src/features/context-bar/DrawerContentHeader';
import { MessagingThemeProvider } from 'src/features/messages/messagingThemeContext';
import { Notes } from 'src/features/notes/Notes';
import { useOpenNoteModal } from 'src/features/notes/useOpenNoteModal';
import { useContextBar } from 'src/hooks/useContextBar';
import { useAppSelector } from 'src/hooks/useStore';
import { selectCurrentUser } from 'src/store/storeUtils';

const URL_PARAM_TO_DRAWER_ID: Record<string, DrawerId> = {
  'internal-chat': DRAWER_IDS.INTERNAL_CHAT,
  notes: DRAWER_IDS.NOTES,
  details: DRAWER_IDS.DETAILS,
};

type ContextBarTabsProps = {
  entityType: 'client' | 'company';
  entityId: string;
  clients?: Client[];
};
export const ContextBarTabs: FC<ContextBarTabsProps> = ({
  entityType,
  entityId,
  clients,
}) => {
  const { EnableContextBarVisualUpgrades, EnableContextBarOtherPage } =
    useContext(FlagsContext);
  const { getActiveDrawer, setLastOpenDrawer } = useContextBar();
  const openNoteModal = useOpenNoteModal({ entityType, entityId });

  const { query } = useContext(RouteContext);
  const { sidebarTab } = query as { sidebarTab?: string };

  const currentUser = useAppSelector(selectCurrentUser);
  const hasAssistantTab = !currentUser?.isClientAccessLimited;

  // Sync URL param to drawer state when flag is off (legacy behavior)
  useEffect(() => {
    if (
      !EnableContextBarOtherPage &&
      sidebarTab &&
      URL_PARAM_TO_DRAWER_ID[sidebarTab]
    ) {
      setLastOpenDrawer(URL_PARAM_TO_DRAWER_ID[sidebarTab]);
    }
  }, [EnableContextBarOtherPage, sidebarTab, setLastOpenDrawer]);

  const activeTabKey = getActiveDrawer(hasAssistantTab);

  const handleTabChange = (tabKey: DrawerId) => {
    setLastOpenDrawer(tabKey);
  };

  const tabs = [
    ...(hasAssistantTab
      ? ([
          {
            key: DRAWER_IDS.INTERNAL_CHAT,
            label: 'Internal chat',
          },
        ] as const)
      : []),
    { key: DRAWER_IDS.NOTES, label: 'Notes' } as const,
    { key: DRAWER_IDS.DETAILS, label: 'Details' } as const,
  ];

  if (EnableContextBarVisualUpgrades) {
    return (
      <div className="h-full w-full bg-gray-100 flex">
        <div className="flex flex-col flex-grow min-w-0">
          <DrawerContentHeader
            drawerId={activeTabKey}
            entityType={entityType}
            onAction={
              activeTabKey === DRAWER_IDS.NOTES
                ? () => openNoteModal(null)
                : undefined
            }
          />
          <div className="w-full flex flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              {activeTabKey === DRAWER_IDS.NOTES && (
                <Notes entityType={entityType} entityId={entityId} />
              )}
              {activeTabKey === DRAWER_IDS.INTERNAL_CHAT && (
                <MessagingThemeProvider isSidebarTheme>
                  <InternalChat entityType={entityType} entityId={entityId} />
                </MessagingThemeProvider>
              )}
              {activeTabKey === DRAWER_IDS.DETAILS && (
                <div className="py-3">
                  <ContextProperties
                    entityType={entityType}
                    entityId={entityId}
                    clients={clients}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-gray-100 flex flex-col">
      <div className="border border-solid border-b border-l-0 border-r-0 border-t-0 border-non-hover-border flex-shrink-0">
        <ResponsiveTabs<DrawerId>
          overflow="scroll"
          tabs={tabs}
          activeTabKey={activeTabKey}
          onTabChange={handleTabChange}
          className="h-12"
          classes={{
            tabButton: 'hover:bg-gray-150',
          }}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {activeTabKey === DRAWER_IDS.NOTES && (
          <Notes entityType={entityType} entityId={entityId} />
        )}
        {activeTabKey === DRAWER_IDS.INTERNAL_CHAT && (
          <MessagingThemeProvider isSidebarTheme>
            <InternalChat entityType={entityType} entityId={entityId} />
          </MessagingThemeProvider>
        )}
        {activeTabKey === DRAWER_IDS.DETAILS && (
          <div className="py-4">
            <ContextProperties
              entityType={entityType}
              entityId={entityId}
              clients={clients}
            />
          </div>
        )}
      </div>
    </div>
  );
};
